// crewbit-admin/src/components/money/ExpenseTableRow.tsx
//
// Single expense row inside ExpensesTable. Click anywhere on the row routes
// the parent to set `?expense_id={id}` which opens MoneyExpenseEditPanel.
//
// Renders pills: AI-categorized, recurring, mileage, HST-exempt, archived,
// needs-review. Date + vendor + category + amount columns + pill column.
//
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §Expenses.7.2.

import { Sparkles, Repeat, Car, ShieldOff, Archive, AlertTriangle } from 'lucide-react';
import { palette, spacing, radii, transition } from '../../styles/tokens';
import { formatMoney } from '../../lib/money';
import { isNeedsReview } from '../../lib/t2125Mapping';
import { getCategoryLabel } from '../../constants/expenseCategories';
import type { Expense } from '../../types';

interface ExpenseTableRowProps {
  expense: Expense;
  onClick: (expense: Expense) => void;
}

const PILL_BASE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 8px',
  borderRadius: radii.full,
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap' as const,
};

function fmtDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ExpenseTableRow({ expense, onClick }: ExpenseTableRowProps) {
  const e = expense;
  const isMileage = (e.mileage_km ?? 0) > 0;
  const archived = e.archived ?? false;
  const needsReview = isNeedsReview(e);

  return (
    <tr
      onClick={() => onClick(e)}
      style={{
        cursor: 'pointer',
        background: palette.white,
        transition: `background ${transition.fast}`,
        opacity: archived ? 0.55 : 1,
      }}
      onMouseEnter={(ev) => (ev.currentTarget.style.background = palette.g50)}
      onMouseLeave={(ev) => (ev.currentTarget.style.background = palette.white)}
    >
      <td
        style={{
          padding: `${spacing.sm + 2}px ${spacing.md}px`,
          fontSize: 13,
          color: palette.g700,
          borderBottom: `1px solid ${palette.g100}`,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {fmtDate(e.date)}
      </td>
      <td
        style={{
          padding: `${spacing.sm + 2}px ${spacing.md}px`,
          fontSize: 13,
          color: palette.g800,
          borderBottom: `1px solid ${palette.g100}`,
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {e.vendor_name ?? <span style={{ color: palette.g400 }}>—</span>}
      </td>
      <td
        style={{
          padding: `${spacing.sm + 2}px ${spacing.md}px`,
          fontSize: 13,
          color: palette.g700,
          borderBottom: `1px solid ${palette.g100}`,
          maxWidth: 240,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <div style={{ fontWeight: 500 }}>{getCategoryLabel(e.category)}</div>
        {e.description && e.description !== e.category && (
          <div
            style={{
              fontSize: 11.5,
              color: palette.g500,
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {e.description}
          </div>
        )}
      </td>
      <td
        style={{
          padding: `${spacing.sm + 2}px ${spacing.md}px`,
          borderBottom: `1px solid ${palette.g100}`,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {needsReview && (
            <span style={{ ...PILL_BASE, background: palette.amberWash, color: palette.amber }}>
              <AlertTriangle size={10} strokeWidth={2.5} />
              Needs review
            </span>
          )}
          {e.ai_categorized && (
            <span style={{ ...PILL_BASE, background: palette.purpleWash, color: palette.purple }}>
              <Sparkles size={10} strokeWidth={2.5} />
              AI
            </span>
          )}
          {e.is_recurring && (
            <span style={{ ...PILL_BASE, background: palette.indigoWash, color: palette.purple }}>
              <Repeat size={10} strokeWidth={2.5} />
              Recurring
            </span>
          )}
          {isMileage && (
            <span style={{ ...PILL_BASE, background: palette.blueWash, color: palette.blue }}>
              <Car size={10} strokeWidth={2.5} />
              {e.mileage_km}km
            </span>
          )}
          {e.is_hst_exempt && (
            <span style={{ ...PILL_BASE, background: palette.g100, color: palette.g600 }}>
              <ShieldOff size={10} strokeWidth={2.5} />
              HST exempt
            </span>
          )}
          {archived && (
            <span style={{ ...PILL_BASE, background: palette.g100, color: palette.g500 }}>
              <Archive size={10} strokeWidth={2.5} />
              Archived
            </span>
          )}
        </div>
      </td>
      <td
        style={{
          padding: `${spacing.sm + 2}px ${spacing.md}px`,
          fontSize: 13.5,
          fontWeight: 600,
          color: palette.g800,
          textAlign: 'right',
          borderBottom: `1px solid ${palette.g100}`,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {formatMoney(e.amount)}
      </td>
    </tr>
  );
}
